import { useState } from 'react';
import { Pencil, Save, X, Mail, Phone, MapPin } from 'lucide-react';
import type { UserDetail } from '../UserDetailPage';

interface Props {
  user: UserDetail;
  isEditing: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (updates: Partial<UserDetail>) => Promise<void>;
  onHardDelete: () => void;
}

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  profileName: string;
  zipCode: string;
  sport: string;
  gender: string;
  ageRange: string;
  handedness: string;
  playStyle: string;
  backhand: string;
};

// Extracted outside the component so React doesn't remount on every render
function FormField({ label, value, formValue, field, type = 'text', icon, isEditing, onChange }: {
  label: string;
  value: string;
  formValue: string;
  field: string;
  type?: string;
  icon?: React.ReactNode;
  isEditing: boolean;
  onChange: (field: string, value: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-400 mb-1">{label}</label>
      {isEditing ? (
        <input
          type={type}
          value={formValue}
          onChange={e => onChange(field, e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
        />
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 text-sm text-slate-200">
          {icon}
          <span>{value || '\u2014'}</span>
        </div>
      )}
    </div>
  );
}

function FormSelect({ label, value, formValue, field, options, isEditing, onChange }: {
  label: string;
  value: string;
  formValue: string;
  field: string;
  options: { value: string; label: string }[];
  isEditing: boolean;
  onChange: (field: string, value: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-400 mb-1">{label}</label>
      {isEditing ? (
        <select
          value={formValue}
          onChange={e => onChange(field, e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
        >
          <option value="">Not set</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <div className="px-3 py-2 text-sm text-slate-200">
          {options.find(o => o.value === value)?.label || value || '\u2014'}
        </div>
      )}
    </div>
  );
}

export function UserProfileTab({ user, isEditing, isSaving, onEdit, onCancel, onSave, onHardDelete }: Props) {
  const [form, setForm] = useState<FormState>({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    mobileNumber: user.mobileNumber || '',
    profileName: user.profileName || '',
    zipCode: user.zipCode || '',
    sport: user.sport || '',
    gender: user.gender || '',
    ageRange: user.ageRange || '',
    handedness: user.handedness || '',
    playStyle: user.playStyle || '',
    backhand: user.backhand || '',
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleFieldChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      // Convert empty strings to null so backend stores NULL (not '')
      // Prevents UNIQUE constraint violations on nullable columns like mobile_number
      const cleaned: Record<string, string | null> = {};
      for (const [key, value] of Object.entries(form)) {
        cleaned[key] = value.trim() === '' ? null : value.trim();
      }
      await onSave(cleaned);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const handleCancel = () => {
    setForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobileNumber: user.mobileNumber || '',
      profileName: user.profileName || '',
      zipCode: user.zipCode || '',
      sport: user.sport || '',
      gender: user.gender || '',
      ageRange: user.ageRange || '',
      handedness: user.handedness || '',
      playStyle: user.playStyle || '',
      backhand: user.backhand || '',
    });
    setSaveError(null);
    onCancel();
  };

  return (
    <div className="space-y-6">
      {/* Header with edit button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Profile Information</h2>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button onClick={handleCancel} disabled={isSaving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors disabled:opacity-50">
                <X className="h-4 w-4" /> Cancel
              </button>
              <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" /> {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button onClick={onEdit} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors">
                <Pencil className="h-4 w-4" /> Edit
              </button>
              <button onClick={onHardDelete} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-800 hover:bg-red-700 text-white rounded-lg transition-colors border border-red-700">
                Hard Delete
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && (
        <div className="px-4 py-2 bg-red-900/20 border border-red-800/50 rounded-lg text-sm text-red-400">{saveError}</div>
      )}

      {/* Personal Info */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6 space-y-6">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Personal</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="First Name" value={user.firstName} formValue={form.firstName} field="firstName" isEditing={isEditing} onChange={handleFieldChange} />
          <FormField label="Last Name" value={user.lastName} formValue={form.lastName} field="lastName" isEditing={isEditing} onChange={handleFieldChange} />
          <FormField label="Email" value={user.email} formValue={form.email} field="email" type="email" icon={<Mail className="h-4 w-4 text-slate-500" />} isEditing={isEditing} onChange={handleFieldChange} />
          <FormField label="Phone" value={user.mobileNumber || ''} formValue={form.mobileNumber} field="mobileNumber" type="tel" icon={<Phone className="h-4 w-4 text-slate-500" />} isEditing={isEditing} onChange={handleFieldChange} />
          <FormField label="Profile Name" value={user.profileName || ''} formValue={form.profileName} field="profileName" isEditing={isEditing} onChange={handleFieldChange} />
          <FormField label="ZIP Code" value={user.zipCode || ''} formValue={form.zipCode} field="zipCode" icon={<MapPin className="h-4 w-4 text-slate-500" />} isEditing={isEditing} onChange={handleFieldChange} />
        </div>
      </div>

      {/* Sport Profile */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6 space-y-6">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Sport Profile</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FormSelect label="Sport" value={user.sport || ''} formValue={form.sport} field="sport" isEditing={isEditing} onChange={handleFieldChange} options={[
            { value: 'TENNIS', label: 'Tennis' },
            { value: 'PICKLEBALL', label: 'Pickleball' },
            { value: 'BOTH', label: 'Both' },
          ]} />
          <FormSelect label="Gender" value={user.gender || ''} formValue={form.gender} field="gender" isEditing={isEditing} onChange={handleFieldChange} options={[
            { value: 'MALE', label: 'Male' },
            { value: 'FEMALE', label: 'Female' },
            { value: 'OTHER', label: 'Other' },
            { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
          ]} />
          <FormSelect label="Age Range" value={user.ageRange || ''} formValue={form.ageRange} field="ageRange" isEditing={isEditing} onChange={handleFieldChange} options={[
            { value: '18-25', label: '18-25' },
            { value: '26-35', label: '26-35' },
            { value: '36-45', label: '36-45' },
            { value: '46-55', label: '46-55' },
            { value: '56-65', label: '56-65' },
            { value: '65+', label: '65+' },
          ]} />
          <FormSelect label="Handedness" value={user.handedness || ''} formValue={form.handedness} field="handedness" isEditing={isEditing} onChange={handleFieldChange} options={[
            { value: 'RIGHT', label: 'Right' },
            { value: 'LEFT', label: 'Left' },
            { value: 'AMBIDEXTROUS', label: 'Ambidextrous' },
          ]} />
          <FormSelect label="Play Style" value={user.playStyle || ''} formValue={form.playStyle} field="playStyle" isEditing={isEditing} onChange={handleFieldChange} options={[
            { value: 'AGGRESSIVE', label: 'Aggressive' },
            { value: 'DEFENSIVE', label: 'Defensive' },
            { value: 'ALL_COURT', label: 'All Court' },
            { value: 'SERVE_VOLLEY', label: 'Serve & Volley' },
          ]} />
          <FormSelect label="Backhand" value={user.backhand || ''} formValue={form.backhand} field="backhand" isEditing={isEditing} onChange={handleFieldChange} options={[
            { value: 'ONE_HANDED', label: 'One-Handed' },
            { value: 'TWO_HANDED', label: 'Two-Handed' },
          ]} />
        </div>
      </div>
    </div>
  );
}
